import { useState, useEffect } from "react";
import { makeRequestAuth } from "../../_config/api";
import { toast } from "react-toastify";
import { X, Save, AlertCircle } from "lucide-react"; // Icons for better UI
// import { showErrorToast, showSuccessToast } from "../../utils/toastUtils";

function UpdateBuyOrderModal({ isOpen, onClose, data, refresh }) {
  const [form, setForm] = useState({
    name: "",
    rate: "",
    amount: "",
    walletType: "",
    status: "",
  });
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (data) {
      setForm({
        name: data.name || "",
        rate: data.rate || "",
        amount: data.amount || "",
        walletType: data.walletType || "USDT",
        status: data.status || "pending",
      });
    }
  }, [data]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await makeRequestAuth("post", "/admin/updateP2PBuyOrder", {
        id: data._id,
        ...form,
      });
      toast.success("Order updated successfully");
      refresh();
      onClose();
    } catch (err) {
      toast.error(err.message || "Update failed");
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-end sm:items-center justify-center p-0 sm:p-4 bg-black/70 backdrop-blur-sm transition-opacity">
      {/* Modal Container */}
      <div className="w-full max-w-lg bg-white dark:bg-[#121212] rounded-t-3xl sm:rounded-2xl shadow-2xl border border-gray-200 dark:border-zinc-800 transform transition-all animate-in fade-in slide-in-from-bottom-10 duration-300">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100 dark:border-zinc-800">
          <div>
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">
              Update Buy Order
            </h2>
            <p className="text-xs text-gray-500 dark:text-zinc-500 mt-0.5">
              ID: {data?._id?.slice(-8).toUpperCase()}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 bg-gray-100 dark:bg-zinc-800 hover:bg-red-50 dark:hover:bg-red-900/20 hover:text-red-500 rounded-full transition-all"
          >
            <X size={20} />
          </button>
        </div>

        {/* Scrollable Body */}
        <div className="p-6 max-h-[70vh] overflow-y-auto space-y-5 custom-scrollbar">
          {/* Name Field */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400 ml-1">
              Customer Name
            </label>
            <input
              name="name"
              value={form.name}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-transparent outline-none transition-all"
            />
          </div>

          {/* Grid for Rate & Amount */}
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400 ml-1">
                Rate
              </label>
              <input
                name="rate"
                type="number"
                value={form.rate}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              />
            </div>
            <div className="space-y-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400 ml-1">
                Amount
              </label>
              <input
                name="amount"
                type="number"
                value={form.amount}
                onChange={handleChange}
                className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none transition-all"
              />
            </div>
          </div>

          {/* Wallet Selection */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400 ml-1">
              Wallet Type
            </label>
            <select
              name="walletType"
              value={form.walletType}
              onChange={handleChange}
              className="w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 outline-none appearance-none cursor-pointer"
            >
              <option value="USDT">USDT</option>
              <option value="TOKEN">TOKEN</option>
            </select>
          </div>

          {/* Status Selection */}
          <div className="space-y-1.5">
            <label className="text-xs font-semibold uppercase tracking-wider text-gray-500 dark:text-zinc-400 ml-1">
              Status Management
            </label>
            <select
              name="status"
              value={form.status}
              onChange={handleChange}
              className={`w-full px-4 py-3 rounded-xl border border-gray-200 dark:border-zinc-700 bg-gray-50 dark:bg-zinc-900 font-medium focus:ring-2 focus:ring-blue-500 outline-none appearance-none cursor-pointer
                ${form.status === "approve" ? "text-green-500" : form.status === "reject" ? "text-red-500" : "text-gray-900 dark:text-white"}`}
            >
              <option value="draft">📝 Draft</option>
              <option value="pending">⏳ Pending</option>
              <option value="requested">📩 Requested</option>
              <option value="process">⚙️ In Process</option>
              <option value="approve">✅ Approve Order</option>
              <option value="reject">❌ Reject Order</option>
            </select>
          </div>
        </div>

        {/* Footer Actions */}
        <div className="flex flex-col sm:flex-row gap-3 px-6 py-6 bg-gray-50 dark:bg-zinc-900/30 border-t border-gray-100 dark:border-zinc-800">
          <button
            onClick={onClose}
            className="order-2 sm:order-1 flex-1 px-6 py-3 text-sm font-bold text-gray-600 dark:text-zinc-400 hover:bg-gray-200 dark:hover:bg-zinc-800 rounded-xl transition-all"
          >
            Go Back
          </button>

          <button
            onClick={handleSubmit}
            disabled={loading}
            className="order-1 sm:order-2 flex-[2] flex items-center justify-center gap-2 px-6 py-3 text-sm font-bold text-white bg-blue-600 hover:bg-blue-700 active:scale-[0.98] rounded-xl shadow-xl shadow-blue-500/25 transition-all disabled:opacity-50 disabled:pointer-events-none"
          >
            {loading ? (
              <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
            ) : (
              <>
                <Save size={18} />
                Confirm Update
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}

export default UpdateBuyOrderModal;
